import { put, select } from "redux-saga/effects";
import { push, getLocation, matchPath } from "connected-react-router";
import { isEqual } from "lodash";

// The query string as the single source of truth for a list page's filter:
//   URL --getFilterByLocationSearch--> filter  (on LOCATION_CHANGE)
//   filter --getQueryByFilter--> URL           (on APPLY_FILTER)
//
// Written for /vacancies (CREW-1169) and lifted here when /vessels adopted the
// same contract (CREW-1024) — one implementation, not one per page, is the
// point CREW-1174 made about the panel itself. Each page keeps its own
// urlFilters.js (the keys and their spelling are the page's business) and its
// own basePageUrl; the two sagas below are what they have in common.
//
// The search box travels in the URL with everything else. It used to be kept
// per user in localStorage, on the reasoning that a shared link should not
// carry somebody else's half-typed vessel name — but the result was worse: the
// link carried an INVISIBLE search instead. Opening `?advertising=true` silently
// re-applied whatever the reader had last typed into their own box, so the same
// link showed a different list to every person, and neither of them could see
// why. What is on screen has to be what the URL says; that is the whole point of
// the contract, and /task-manager already worked this way.
//
// A page opts in by passing `actions.changeSearch`; `selectFeature` must then
// expose `search` on its slice.

/**
 * createUrlFilterSagas
 * @param {String} basePageUrl        e.g. "/vessels"
 * @param {Function} selectFeature    state → the feature slice ({filter, appliedFilter})
 * @param {Object} urlFilters         {getFilterByLocationSearch, getQueryByFilter, withLabelsFrom?}
 * @param {Object} actions            {setFilter, loadItemsRequest, changeSearch?}
 * @returns {{pageInit: Function, syncFilterToUrl: Function, didOpenListPage: Function}}
 */
export const createUrlFilterSagas = ({ basePageUrl, selectFeature, urlFilters, actions }) => {
  const {
    getFilterByLocationSearch,
    getQueryByFilter,
    withLabelsFrom = (filter) => filter,
  } = urlFilters;

  const didOpenListPage = (pathname, isFirstRendering) =>
    matchPath(pathname, { exact: true, path: basePageUrl }) ||
    // "|| isFirstRendering" for the case where you open a form and reload the
    // page — the list behind the form must still be there.
    (isFirstRendering &&
      matchPath(pathname, { path: `${basePageUrl}/:id?/(new|edit|delete)` }));

  /**
   * pageInit
   * Hydrate the filter from the URL and fetch. Runs on every location change
   * that lands on the page: first render, in-app navigation, back/forward, a
   * link shared by a colleague, and our own push from syncFilterToUrl.
   */
  function* pageInit({
    payload: {
      isFirstRendering,
      location: { pathname, search },
    },
  }) {
    if (!didOpenListPage(pathname, isFirstRendering)) return;

    const feature = yield select(selectFeature);
    const { appliedFilter } = feature;
    const parsed = getFilterByLocationSearch(search);
    // `search` rides in the same query string but lives beside the filter in
    // state, so it is split back out here rather than leaking into the drawer.
    const { search: urlSearch = "", ...urlOnlyFilter } = parsed;
    // The URL carries ids only, so what comes back has no labels. Carry them
    // over from the filter we already hold, or the drawer's chips lose their
    // names on our own push (CREW-1208).
    const urlFilter = withLabelsFrom(urlOnlyFilter, appliedFilter);

    // Only replace the filter (and reset paging) when the URL actually
    // differs — closing an edit modal lands back on the same query and must
    // keep the page the user was on.
    if (!isEqual(urlFilter, appliedFilter || {})) {
      yield put(actions.setFilter(urlFilter));
    }
    // Same rule for the box: hydrate it only when the URL disagrees with it, so
    // reopening the same query does not retype what is already there.
    if (actions.changeSearch && urlSearch !== (feature.search || "")) {
      yield put(actions.changeSearch(urlSearch));
    }
    yield put(actions.loadItemsRequest());
  }

  /**
   * syncFilterToUrl
   * Applying a filter never fetches directly: it writes the filter into the
   * URL and the resulting LOCATION_CHANGE hydrates and fetches, so there is
   * one code path. When the URL already spells the same filter (re-applying
   * an identical one, or clearing an already-empty filter) no navigation
   * happens, so fetch here — APPLY_FILTER's reducer has already set isLoading.
   */
  function* syncFilterToUrl() {
    try {
      const location = yield select(getLocation);

      // Applied from behind a modal route (/vessels/5/edit): there is no list
      // URL to own here, and pushing one would close the modal.
      if (!matchPath(location.pathname, { exact: true, path: basePageUrl })) {
        yield put(actions.loadItemsRequest());
        return;
      }

      const feature = yield select(selectFeature);
      const query = getQueryByFilter(
        actions.changeSearch ? { ...feature.filter, search: feature.search } : feature.filter,
      );

      // Compare through the serialiser, not raw strings — the same filter can
      // be spelled several ways in the address bar (?role=2,1, ?role=1&role=2,
      // keys in another order) and all of them mean "we are already here".
      if (getQueryByFilter(getFilterByLocationSearch(location.search)) === query) {
        yield put(actions.loadItemsRequest());
        return;
      }

      yield put(push(query ? `${basePageUrl}?${query}` : basePageUrl));
    } catch (e) {
      console.error(e);
    }
  }

  return { pageInit, syncFilterToUrl, didOpenListPage };
};

export default createUrlFilterSagas;
